// BettingInterface Component
// High Stakes Poker - Choose your bet level and send match request

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeepPileEntry, BetLevel } from '../../../types/swipe';
import { BET_LEVEL_DETAILS } from '../../../constants/swipe';
import {
  BettingOverlay,
  BettingContainer,
  BettingHeader,
  BettingCloseButton,
  BettingTitle,
  BettingSubtitle,
  ProfileCardPreview,
  ProfileImage,
  ProfileOverlay,
  BettingProfileName,
  BettingProfileInfo,
  BetLevelSection,
  BetLevelTitle,
  BetLevelGrid,
  BetLevelCard,
  BetLevelIcon,
  BetLevelLabel,
  BetLevelDescription,
  BetLevelRecommendation,
  MessageSection,
  MessageLabel,
  MessageTextarea,
  CharacterCount,
  BetLevelStats,
  StatBadge,
  SubmitSection,
  SubmitButton,
  SubmitButtonIcon,
  CancelButton
} from '../../../styles/Swipe/BettingInterface.styles';

interface BettingInterfaceProps {
  entry: KeepPileEntry;
  onClose: () => void;
  onSubmit: (betLevel: BetLevel, message: string) => Promise<void>;
}

export const BettingInterface: React.FC<BettingInterfaceProps> = ({
  entry,
  onClose,
  onSubmit
}) => {
  const navigate = useNavigate();
  const [selectedBet, setSelectedBet] = useState<BetLevel | null>(null);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const maxMessageLength = 300;

  // Bet level details
  const betLevels: Array<{ level: BetLevel; color: string }> = [
    { level: 'low', color: '#4ECDC4' },
    { level: 'medium', color: '#F7B731' },
    { level: 'high', color: '#FF6B6B' }
  ];

  // Get recommended bet level based on compatibility
  const getRecommendedBet = (): BetLevel => {
    const compatibility = entry.profile.compatibilityScore || 0;
    if (compatibility >= 85) return 'high';
    if (compatibility >= 70) return 'medium';
    return 'low';
  };

  const recommendedBet = getRecommendedBet();

  // Handle bet selection
  const handleBetSelect = (bet: BetLevel) => {
    setSelectedBet(bet);
    
    // Set default message based on bet level
    const defaultMessages: Record<BetLevel, string> = {
      low: `Hey ${entry.profile.firstName}! Would love to grab a coffee and chat sometime. ☕`,
      medium: `Hi ${entry.profile.firstName}! I'd love to take you out for dinner. What do you think? 🍷`,
      high: `${entry.profile.firstName}, I'm really interested in getting to know you better. Would you be open to a video call first? 🎯`
    };
    setMessage(defaultMessages[bet]);
  };

  // Handle message change
  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newMessage = e.target.value;
    if (newMessage.length <= maxMessageLength) {
      setMessage(newMessage);
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!selectedBet) {
      alert('Please select a bet level');
      return;
    }

    if (!message.trim()) {
      alert('Please write a message');
      return;
    }

    try {
      setIsSubmitting(true);
      await onSubmit(selectedBet, message);
      // Success handled by parent component
    } catch (error) {
      console.error('Error submitting bet:', error);
      alert('Failed to send match request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const compatibility = entry.profile.compatibilityScore || 0;

  return (
    <BettingOverlay onClick={handleBackdropClick}>
      <BettingContainer>
        {/* Header */}
        <BettingHeader>
          <div>
            <BettingTitle>🎯 High Stakes Poker</BettingTitle>
            <BettingSubtitle>Choose your bet level and make your move</BettingSubtitle>
          </div>
          <BettingCloseButton onClick={onClose}>×</BettingCloseButton>
        </BettingHeader>

        {/* Profile Preview */}
        <ProfileCardPreview>
          <ProfileImage $imageUrl={entry.profile.photos[0]}>
            <ProfileOverlay>
              <BettingProfileName>
                {entry.profile.firstName}, {entry.profile.age}
              </BettingProfileName>
              <BettingProfileInfo>
                <span>{entry.profile.location}</span>
                <span>•</span>
                <span>{compatibility}% Match</span>
              </BettingProfileInfo>
            </ProfileOverlay>
          </ProfileImage>
        </ProfileCardPreview>

        {/* Stats */}
        <BetLevelStats>
          <StatBadge>
            💯 {compatibility}% Compatible
          </StatBadge>
          <StatBadge>
            🎴 {entry.profile.personalityType}
          </StatBadge>
          <StatBadge>
            🔥 {entry.profile.interests.slice(0, 2).join(', ')}
          </StatBadge>
        </BetLevelStats>

        {/* Bet Level Selection */}
        <BetLevelSection>
          <BetLevelTitle>Select Your Bet Level</BetLevelTitle>
          <BetLevelGrid>
            {betLevels.map(({ level, color }) => {
              const details = BET_LEVEL_DETAILS[level];
              const isRecommended = level === recommendedBet;
              const isSelected = selectedBet === level;

              return (
                <BetLevelCard
                  key={level}
                  $isSelected={isSelected}
                  $betLevel={level}
                  onClick={() => handleBetSelect(level)}
                >
                  <BetLevelIcon>{details.icon}</BetLevelIcon>
                  <BetLevelLabel>{details.label}</BetLevelLabel>
                  <BetLevelDescription>{details.description}</BetLevelDescription>
                  {isRecommended && (
                    <BetLevelRecommendation>
                      ⭐ Recommended
                    </BetLevelRecommendation>
                  )}
                </BetLevelCard>
              );
            })}
          </BetLevelGrid>
        </BetLevelSection>

        {/* Message Section */}
        {selectedBet && (
          <MessageSection>
            <MessageLabel>
              Your Opening Message
              <CharacterCount $isNearLimit={message.length >= maxMessageLength - 20}>
                {message.length}/{maxMessageLength}
              </CharacterCount>
            </MessageLabel>
            <MessageTextarea
              value={message}
              onChange={handleMessageChange}
              placeholder={`Write a personalized message to ${entry.profile.firstName}...`}
              rows={4}
              maxLength={maxMessageLength}
            />
          </MessageSection>
        )}

        {/* Submit Section */}
        <SubmitSection>
          <CancelButton onClick={onClose} disabled={isSubmitting}>
            Cancel
          </CancelButton>
          <SubmitButton
            onClick={handleSubmit}
            disabled={!selectedBet || !message.trim() || isSubmitting}
          >
            <SubmitButtonIcon>🃏</SubmitButtonIcon>
            {isSubmitting ? 'Sending...' : 'Play Your Card'}
          </SubmitButton>
        </SubmitSection>
      </BettingContainer>
    </BettingOverlay>
  );
};

